
import { useState } from 'react';
import { mapboxgl } from '../utils/mapboxConfig';
import { useToast } from '@/hooks/use-toast';

interface UseGeolocationProps {
  map: React.MutableRefObject<mapboxgl.Map | null>;
  mapLoaded: boolean;
  userMarkerRef: React.MutableRefObject<mapboxgl.Marker | null>;
  addMarkers: () => void;
}

export const useGeolocation = ({ map, mapLoaded, userMarkerRef, addMarkers }: UseGeolocationProps) => {
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const { toast } = useToast();
  
  // Get user's location - only when explicitly requested
  const getUserLocation = () => {
    if (!map.current || !mapLoaded) return;
    
    setIsLocating(true);
    
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { longitude, latitude } = position.coords;
          setUserLocation([longitude, latitude]);
          
          // Wait for the next tick to ensure the map is ready
          setTimeout(() => {
            if (!map.current) return;
            
            // Fly to the user's location
            map.current.flyTo({
              center: [longitude, latitude],
              zoom: 10,
              essential: true
            });
            
            // Remove previous user marker if it exists
            if (userMarkerRef.current) {
              userMarkerRef.current.remove();
            }
            
            // Add a special marker for user location
            const el = document.createElement('div');
            el.className = 'mapboxgl-user-location-marker';
            
            const markerEl = document.createElement('div');
            markerEl.className = 'flex items-center justify-center w-8 h-8';
            markerEl.innerHTML = `
              <div class="absolute animate-ping w-6 h-6 rounded-full bg-blue-500 opacity-30"></div>
              <div class="relative flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="4"></circle>
                </svg>
              </div>
            `;
            el.appendChild(markerEl);
            
            userMarkerRef.current = new mapboxgl.Marker({
              element: el,
              anchor: 'center' // User location marker should be centered
            })
              .setLngLat([longitude, latitude])
              .addTo(map.current);
            
            // Make sure we redraw ride markers after setting user location
            addMarkers();
            
            setIsLocating(false);
            
            toast({
              title: "Location found",
              description: "Map has been centered to your current location."
            });
          }, 100);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setIsLocating(false);
          
          toast({
            title: "Location error",
            description: `Couldn't access your location: ${error.message}`,
            variant: "destructive"
          });
        },
        { 
          enableHighAccuracy: true, 
          timeout: 10000, 
          maximumAge: 0 
        }
      );
    } else {
      toast({
        title: "Location not supported",
        description: "Your browser doesn't support geolocation.",
        variant: "destructive"
      });
      setIsLocating(false);
    }
  };
  
  return { userLocation, isLocating, getUserLocation };
};
